import { useMemo } from "react";

import {
  type Permission,
  PERMISSIONS,
} from "@/features/roles/constants/permissions";
import { coversRole } from "@/features/roles/helpers/roles";
import { useGetMe } from "@/hooks/api/user/useGetMe";
import { useGlobalStore } from "@/stores/global";
import { CONTACT_VISIBILITY, type RoleGrant } from "@/types/role";
import { ORG_ROLE, OrgRole, WORKSPACE_ROLE, WorkspaceRole } from "@/types/user";

interface RolesState {
  // Role values
  orgRole: OrgRole | null;
  workspaceRole: WorkspaceRole | null;

  // Org role checks
  isOrgAdmin: boolean;
  isOrgMember: boolean;

  // Workspace role checks
  isWorkspaceAdmin: boolean;
  isWorkspaceMember: boolean;
  isWorkspaceAgent: boolean;

  // What the user's role lets them do in the active workspace
  permissions: Permission[];
  can: (permission: Permission) => boolean;
  // The viewer's own role, as far as giving roles out goes; null off the workspace
  grant: RoleGrant | null;
  // Whether they may give `role` out, or change someone who holds it
  covers: (role: RoleGrant) => boolean;
}

export const useRoles = (): RolesState => {
  const loggedInUser = useGlobalStore((state) => state.loggedInUser);
  const activeWorkspaceId = useGlobalStore((state) => state.activeWorkspaceId);
  const { data: me } = useGetMe();
  const access = me?.access;

  return useMemo(() => {
    const orgRole =
      access?.organizationRole ?? loggedInUser?.role ?? "organization:admin";
    const workspaceRole =
      access?.base ??
      loggedInUser?.workspaces?.find((w) => w.id === activeWorkspaceId)?.role ??
      "workspace:admin";
    // Org admins hold everything. `access: null` is the server saying this user
    // isn't a member here. Bootstrap waits for `/users/me`, so nothing renders
    // sooner.
    const permissions: Permission[] =
      orgRole === ORG_ROLE.ADMIN
        ? [...PERMISSIONS]
        : (access?.permissions ?? []);
    const grant: RoleGrant | null =
      access === null
        ? null
        : {
            base: workspaceRole,
            contactVisibility:
              access?.contactVisibility ?? CONTACT_VISIBILITY.ALL,
            permissions,
          };

    return {
      // Role values
      orgRole,
      workspaceRole,

      // Org role checks
      isOrgAdmin: orgRole === ORG_ROLE.ADMIN,
      isOrgMember: orgRole === ORG_ROLE.MEMBER,

      // Workspace role checks
      isWorkspaceAdmin: workspaceRole === WORKSPACE_ROLE.ADMIN,
      isWorkspaceMember: workspaceRole === WORKSPACE_ROLE.MEMBER,
      isWorkspaceAgent: workspaceRole === WORKSPACE_ROLE.AGENT,

      permissions,
      can: (permission: Permission) => permissions.includes(permission),
      grant,
      covers: (role: RoleGrant) => coversRole(grant, role),
    };
  }, [loggedInUser, activeWorkspaceId, access]);
};
